import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import { youBadge, type AccountState } from './accountState';
import type { AccountMenuSurface } from './useAccountMenuSurface';
import { V4YouAccountCard } from './V4YouAccountCard';

/**
 * THE anon account menu — a person-glyph avatar that opens an in-place, non-modal
 * disclosure carrying `V4YouAccountCard` (the same anon ask the You tab shows):
 * "Save your board(s)", the "✓/⚠ Sent" receipt once she has given us an address,
 * and — in EVERY signed-out state, including no-board — a wired "Sign in".
 *
 * Shared by the board slot (`V4BoardAccountSlot`) and the home shell slot
 * (`V4HomeAccountSlot`, which lights up homepage/People/Calendar/Gift-Guides), so
 * the disclosure, the single-surface handoff, and the focus discipline are defined
 * ONCE and cannot drift (extracted when #398's board menu was extended, per the
 * Codex plan review: "a shared trigger component, not a fork"). The host owns the
 * SURFACE state machine (`useAccountMenuSurface`) + its capture/sign-in wiring; this
 * component is the pure trigger + popover + focus a11y.
 *
 * A11y: the avatar is a disclosure trigger (`aria-haspopup="dialog"`,
 * `aria-expanded`, `aria-controls`); the menu is a non-modal `dialog`; focus moves
 * into it on open and returns to the avatar when the account UI closes back to the
 * page (surface `none`) — whether via Esc / outside-click / re-tap, OR after a
 * sign-in modal / capture sheet the menu opened (those `aria-modal` dialogs own
 * focus while up, via their own `useDialogA11y`; the avatar is restored only once
 * they close). A capture sheet opened WITHOUT the menu (an idle dwell) never marks
 * the account UI active, so it can't yank focus to an avatar the user never touched.
 */

/* ── styles ───────────────────────────────────────────────────────────── */

const Slot = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

// Cream disc with a warm border + person glyph, matching the signed-in avatar so
// the header slot doesn't reflow on sign-in.
const Avatar = styled.button`
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  background: ${({ theme }) => theme.color.creamLight};
  color: ${({ theme }) => theme.color.clay};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.clay};
    outline-offset: 2px;
  }
`;

const Dot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 10px;
  height: 10px;
  border-radius: 9999px;
  background: ${({ theme }) => theme.color.errorRed};
  border: 2px solid ${({ theme }) => theme.color.creamLight};
`;

const CountBadge = styled(Dot)`
  min-width: 17px;
  height: 17px;
  top: -5px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 10.5px;
  font-weight: 600;
  color: #ffffff;
  padding: 0 3px;
`;

/* The account menu surface. `V4YouAccountCard` is rendered `bare` (no chrome of
   its own), so this popover draws the single cream surface for every state —
   including the no-board state, whose bare sign-in row would otherwise float. */
const Popover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  width: 288px;
  padding: 14px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.creamLight};
  border: 1px solid ${({ theme }) => theme.color.warmBorder};
  box-shadow: 0 16px 32px rgba(80, 40, 20, 0.16);
  text-align: left;
`;

const PersonIcon: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

/* ── component ────────────────────────────────────────────────────────── */

export interface V4AccountMenuProps {
  state: AccountState;
  /** The host's single-surface state — the menu renders iff `account-menu`, and
   *  focus returns to the avatar when the account UI closes to `none`. */
  surface: AccountMenuSurface;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  /** Menu "Save" → the host's capture sheet. */
  onOpenCapture: () => void;
  /** Menu / sheet "Sign in" → the host's sign-in surface. */
  onSignIn: () => void;
  onResend?: () => void;
  onFixEmail?: (email: string) => void;
  /** Unique per surface — the dialog id AND its data-testid
   *  ('v4-board-account-menu' / 'v4-home-account-menu'). */
  menuId: string;
  /** Test-hook prefix for the avatar/dot/count ('v4-board' / 'v4-home'), keeping
   *  each surface's existing hooks stable. */
  testIdPrefix: string;
  ariaLabel?: string;
}

export const V4AccountMenu: React.FC<V4AccountMenuProps> = ({
  state,
  surface,
  onToggleMenu,
  onCloseMenu,
  onOpenCapture,
  onSignIn,
  onResend,
  onFixEmail,
  menuId,
  testIdPrefix,
  ariaLabel = 'Your account',
}) => {
  const slotRef = useRef<HTMLDivElement | null>(null);
  const avatarRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // True while the account UI owns the flow — the menu, OR a sheet/modal the menu
  // opened from it. Kept across the menu→sheet/modal handoff so the avatar regains
  // focus when that surface finally closes back to the page.
  const accountUiActive = useRef(false);

  const menuOpen = surface === 'account-menu';

  useEffect(() => {
    if (menuOpen) {
      accountUiActive.current = true;
      const first = popoverRef.current?.querySelector<HTMLElement>(
        'button, a[href], input, [tabindex]:not([tabindex="-1"])',
      );
      first?.focus();
    } else if (surface === 'none') {
      if (accountUiActive.current) {
        avatarRef.current?.focus();
        accountUiActive.current = false;
      }
    }
    // surface === 'capture-sheet' | 'sign-in': that surface owns focus itself, so
    // do nothing and keep accountUiActive so the eventual close restores the avatar.
  }, [menuOpen, surface]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (slotRef.current && !slotRef.current.contains(e.target as Node)) {
        onCloseMenu();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMenu();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen, onCloseMenu]);

  const badge = youBadge(state);
  return (
    <Slot ref={slotRef} data-testid={`${testIdPrefix}-avatar`}>
      <Avatar
        ref={avatarRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={menuOpen}
        aria-controls={menuId}
        onClick={onToggleMenu}
      >
        <PersonIcon />
        {badge.kind === 'dot' ? <Dot data-testid={`${testIdPrefix}-dot`} /> : null}
        {badge.kind === 'count' ? (
          <CountBadge data-testid={`${testIdPrefix}-count`}>{badge.count}</CountBadge>
        ) : null}
      </Avatar>
      {menuOpen ? (
        <Popover
          id={menuId}
          ref={popoverRef}
          role="dialog"
          aria-modal="false"
          aria-label={ariaLabel}
          data-testid={menuId}
        >
          {/* The anon ask, laid out as a dropdown. Renders a wired "Sign in" in
              EVERY signed-out state (save-boards / sent / no-board), so nesting it
              never removes the door. */}
          <V4YouAccountCard
            state={state}
            layout="dropdown"
            bare
            onOpenCapture={onOpenCapture}
            onSignIn={onSignIn}
            onResend={onResend}
            onFixEmail={onFixEmail}
          />
        </Popover>
      ) : null}
    </Slot>
  );
};

export default V4AccountMenu;
